import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AxiosError, AxiosResponse } from 'axios';
import set from 'lodash-es/set';

import { QuotationService } from 'app/services/quotation.service';
import {
  isCookiesPopupActive,
  isGetLastForOperatorDisabled,
} from 'app/services/env.service';
import { DictService } from 'app/services/dict.service';
import {
  getQuotationId,
  getQuotationStatus,
  QuotationActions,
  createNewQuotation as createNewQuotationAction,
  createNewQuotationForPartner,
  finalizeQuotationInitialization,
} from 'app/store/data/quotation';
import { getUserData, selectIsOperator } from 'app/store/data/user';
import {
  AcquisitionActions,
  selectAcquisitionParams,
} from 'app/store/data/acquisition';
import {
  NotificationActions,
  NotificationShowActionPayload,
} from 'app/store/data/notification';
import { ThunkAnyDispatch } from 'app/store/helpers';
import { QuotationConfigActions } from 'app/store/data/quotationConfig';
import { QuotationInterface } from 'app/shared/interfaces/Quotation.interface';
import {
  LabelEnum,
  MessageTypeEnum,
  RouteEnum,
  VehicleTypeEnum,
  QuotationType,
  FieldsEnum,
  StatusCodeEnum,
} from 'app/shared/enums';
import { isCookieEnabledInBrowser } from 'app/shared/helpers/isCookieEnabledInBrowser';
import isMobileOrTablet from 'app/shared/helpers/isMobileOrTablet';
import isBot from 'app/shared/helpers/isBot';
import redirectToAztec from 'app/shared/helpers/redirectToAztec';
import {
  checkIQueryParamFromUrl,
  getAcquisitionParams,
} from 'app/shared/helpers/getAcquisitionParams';
import { addDataLayerFlowTypeEvent } from 'app/shared/helpers/addDataLayerFlowTypeEvent';
import { afterCalculationStatuses } from 'app/shared/helpers/quotation';
import { getPartnerLinkParams } from 'app/shared/helpers/getPartnerLinkParams';
import { CarVersionEtResponseInterface } from 'app/shared/interfaces/CarVersionEtResponse.interface';
import removeQueryParams from 'app/shared/helpers/removeQueryParams';
import {
  CalculationBaseViewLocationStateProps,
  CalculationBaseViewProps,
} from './CalculationBaseView.interfaces';
import { useQuery } from 'app/shared/hooks/useQuery';
import { reportMessage } from 'app/services/errorReporter.service';
import { botQuotation } from './botQuotation';

export function useCalculationBaseView({
  location,
  history,
}: CalculationBaseViewProps) {
  const dispatch = useDispatch<ThunkAnyDispatch>();
  const quotationStatus = useSelector(getQuotationStatus);
  const quotationIdFromStore = useSelector(getQuotationId);
  const acquisitionParams = useSelector(selectAcquisitionParams);
  const user = useSelector(getUserData);
  const isOperator = useSelector(selectIsOperator);
  const showNotification = (payload: NotificationShowActionPayload) =>
    dispatch(NotificationActions.showNotification(payload));
  const finalizeQuotationInitializationAction = (
    quotation: QuotationInterface
  ) => dispatch(finalizeQuotationInitialization(quotation));
  const createQuotationAction = (vehicleType?: VehicleTypeEnum) =>
    dispatch(createNewQuotationAction(vehicleType));
  const createNewQuotationForPartnerAction = ({
    brand,
    partnerId,
  }: {
    brand: string;
    partnerId: string;
  }) => dispatch(createNewQuotationForPartner({ brand, partnerId }));
  const setQuotation = (quotation: QuotationInterface) =>
    dispatch(QuotationActions.setQuotation(quotation));
  const setActiveQuotation = (payload: QuotationType) =>
    dispatch(QuotationConfigActions.setActiveQuotation(payload));
  const productId = useQuery().productId as string | null;
  const offerEmailId = useQuery().offerEmailId as string | null;

  const disabledCookies = isCookiesPopupActive && !isCookieEnabledInBrowser();
  const isBotUser = isBot();

  const createNewCalculation = JSON.parse(
    sessionStorage.getItem('createNewCalculation') || 'false'
  );

  const getShouldShowResults = () => {
    const shouldClearForm =
      location && location.state && location.state.clearForm;

    return (
      quotationStatus &&
      afterCalculationStatuses.includes(quotationStatus) &&
      !shouldClearForm &&
      !createNewCalculation
    );
  };

  const initQuotation = () => {
    setActiveQuotation(QuotationType.Vehicle);

    if (isBotUser) {
      setQuotation(botQuotation as QuotationInterface);
      return;
    }

    const state =
      location && location.state
        ? location.state
        : ({} as CalculationBaseViewLocationStateProps);
    const { quotationId: recoveryKey, ignoreDisableGetLastForOperator } = state;
    const linkFromPartner = getPartnerLinkParams();
    const vehicleType = isMotorPath() ? VehicleTypeEnum.Motorcycle : undefined;
    const shouldGetQuotationById =
      location.pathname !== RouteEnum.CALCULATION_FORM &&
      quotationIdFromStore !== '';

    const shouldDisableGetLastForOperator =
      isGetLastForOperatorDisabled && !ignoreDisableGetLastForOperator;

    if (ignoreDisableGetLastForOperator) {
      let state = { ...location.state };
      delete state.ignoreDisableGetLastForOperator;
      history.replace({ ...history.location, state });
    }

    const shouldCreateNewQuotationForOperator =
      isOperator &&
      shouldDisableGetLastForOperator &&
      !linkFromPartner &&
      !recoveryKey &&
      !shouldGetQuotationById;

    if (shouldCreateNewQuotationForOperator || createNewCalculation) {
      createNewQuotation();
    } else if (linkFromPartner) {
      createNewQuotationFromPartnerLink(
        linkFromPartner.brand,
        linkFromPartner.partnerId
      );
    } else {
      const quotationFetching = shouldGetQuotationById
        ? QuotationService.getQuotationById(quotationIdFromStore)
        : recoveryKey
        ? QuotationService.getQuotationById(
            recoveryKey,
            productId,
            offerEmailId
          )
        : QuotationService.getLast(vehicleType);

      quotationFetching
        .then(handleQuotationFetchSuccess)
        .catch(handleQuotationFetchError);
    }
  };

  const isMotorPath = () => {
    return location.pathname === RouteEnum.MOTORCYCLES;
  };

  const removeCheckDataParam = () => {
    const newQueryString = removeQueryParams(['cd']);

    history.replace(`${location.pathname}${newQueryString}`);
  };

  const handleQuotationFetchSuccess = async ({
    data: quotation,
  }: AxiosResponse<QuotationInterface>) => {
    const {
      car,
      id: quotationId,
      number: quotationNumber,
      vehicleType,
    } = quotation;

    if (isMotorPath()) {
      history.replace(RouteEnum.HOME);
    }

    addDataLayerFlowTypeEvent({
      isDefault: true,
      quotationNumber,
      quotationId,
      vehicleType,
    });
    readAcquisitionParams(quotation.id, quotation?.userId);

    const shouldCheckCarDataByNatCode =
      car?.versionEt &&
      car.versionEt !== 'no_car_type' &&
      car?.fuelType &&
      !car.fuelType.includes('_LPG');

    if (shouldCheckCarDataByNatCode && !isBotUser) {
      const updatedQuotation = await checkCarDataByFuelNatCode(quotation);

      finalizeQuotationInitializationAction(updatedQuotation);
    } else {
      finalizeQuotationInitializationAction(quotation);
    }
  };

  const handleQuotationFetchError = (error: AxiosError) => {
    const {
      REACT_APP_REDIRECT_TO_AZTEC_ENABLED: redirectToAztecEnabled,
    } = process.env;

    if (isMobileOrTablet() && redirectToAztecEnabled === 'true') {
      redirectToAztec(quotationIdFromStore);
    }

    if (error.response && error.response.status === StatusCodeEnum.Forbidden) {
      showNotification({
        content: LabelEnum.CalculationFetchFailedNoPermission,
        type: MessageTypeEnum.Danger,
      });
    }

    removeCheckDataParam();
    createNewQuotation();
  };

  const createNewQuotation = () => {
    const vehicleType = isMotorPath() ? VehicleTypeEnum.Motorcycle : undefined;

    createQuotationAction(vehicleType).then((response) => {
      if (response) {
        const {
          id: quotationId,
          number: quotationNumber,
          vehicleType,
        } = response.data;

        if (isMotorPath()) {
          history.replace(RouteEnum.HOME);
        }

        addDataLayerFlowTypeEvent({
          isDefault: true,
          quotationNumber,
          quotationId,
          vehicleType,
        });
        readAcquisitionParams(quotationId, response?.data?.userId);
      }
    });
  };

  const createNewQuotationFromPartnerLink = (
    brand: string,
    partnerId: string
  ) => {
    createNewQuotationForPartnerAction({
      brand,
      partnerId,
    })
      .then((response) => {
        if (response) {
          const {
            id: quotationId,
            number: quotationNumber,
            vehicleType,
          } = response.data;

          addDataLayerFlowTypeEvent({
            isDefault: true,
            quotationNumber,
            quotationId,
            vehicleType,
          });
          readAcquisitionParams(quotationId, response?.data?.userId);
        }
      })
      .catch(() => {
        history.replace(RouteEnum.CALCULATION_FORM);
      });
  };

  const readAcquisitionParams = (
    quotationId: string,
    quotationUserId?: string
  ) => {
    if (isBotUser) {
      return;
    }

    checkIQueryParamFromUrl();

    if (!acquisitionParams) {
      const acquisitionParams = getAcquisitionParams();
      QuotationService.setAcquisitionParams(
        quotationId,
        acquisitionParams
      ).catch((error) => {
        reportMessage(
          `Acquisition error, user id: ${
            user?.id
          }, quotationUserId: ${quotationUserId}, ${JSON.stringify(
            acquisitionParams
          )}, ${JSON.stringify(error)}`,
          'warning'
        );
      });

      dispatch(AcquisitionActions.setAcquisitionParams(acquisitionParams));
    }
  };

  const checkCarDataByFuelNatCode = async (quotation: QuotationInterface) => {
    const { car } = quotation;

    const { data } = ((await DictService.getDictionary(
      `car_model_versions/${car.versionEt}`,
      {
        fuel: car.fuelType,
      }
    )) as unknown) as AxiosResponse<CarVersionEtResponseInterface>;

    if (car.make !== data.makeNatcode) {
      set(quotation, FieldsEnum.CarMake, data.makeNatcode);
    }

    if (car.model.split('-')[1] !== data.modelName) {
      set(
        quotation,
        FieldsEnum.CarModel,
        `${data.makeNatcode}-${data.modelName}`
      );
    }

    if (car.engineVolume !== data.engineCapacity) {
      set(quotation, 'car.engineVolume', data.engineCapacity);
    }

    set(
      quotation,
      FieldsEnum.CarFuelType,
      data.formFuelTypeCode || data.fuelNatCode
    );

    return quotation;
  };

  useEffect(() => {
    if (disabledCookies) {
      return;
    }

    initQuotation();

    window.addEventListener('beforeunload', (event) => {
      sessionStorage.removeItem('createNewCalculation');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location && location.state && location.state.reload) {
      let state = { ...location.state };
      delete state.reload;
      history.replace({ ...history.location, state });
      history.go(0);
    }
  }, [location, history]);

  return {
    disabledCookies,
    getShouldShowResults,
    quotationIdFromStore,
  };
}
